/**
 * POPULATE BRANDS PAGE
 * 
 * Temporary admin page to populate brand pages from existing items.
 * 
 * SETUP:
 * 1. Save this as src/pages/PopulateBrands.tsx
 * 2. Add route to App.tsx: <Route path="/admin/populate-brands" element={<PopulateBrands />} />
 * 3. Visit /admin/populate-brands and click the button
 * 4. Delete this file when done!
 */

import { useState } from 'react';
import { getDocs, doc, setDoc, getDoc, collectionGroup, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

export default function PopulateBrands() {
  const [status, setStatus] = useState<string>('Ready to populate brands');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  const [topBrands, setTopBrands] = useState<{name: string, count: number, types: string}[]>([]);

  // Helper: Create URL-friendly slug. Mirrors src/utils/polishSlug.ts:slugify —
  // normalize+strip accents so "Pénélope" → "penelope" instead of "pnlope".
  // Also converts "&" → " and " so "Lantern & Wren" → "lantern-and-wren".
  function createBrandSlug(brandName: string): string {
    return brandName
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  const addLog = (message: string) => {
    setResults(prev => [...prev, message]);
    console.log(message);
  };

  const populateBrands = async () => {
    setIsRunning(true);
    setResults([]);
    setTopBrands([]);
    
    addLog('🏰 Brand Populator Starting...');

    // Track brands we find
    const brandMap = new Map<string, { name: string; itemCount: number; itemTypes: Set<string> }>();
    const userItemCounts = new Map<string, Set<string>>();  // Unique users who OWN items
    const wishlistItemCounts = new Map<string, number>();    // Total ITEMS on wishlists
    const collectionNames = new Map<string, Set<string>>();

    try {
      setStatus('Scanning all items...');
      addLog('📦 Scanning all items across all users...');

      // Query ALL items using collectionGroup
      const itemsSnapshot = await getDocs(collectionGroup(db, 'items'));
      
      addLog(`Found ${itemsSnapshot.size} total items to scan`);

      let processed = 0;

      itemsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const brand = data.brand;
        
        if (!brand || brand.trim() === '') {
          processed++;
          return;
        }

        const slug = createBrandSlug(brand);
        const userId = docSnap.ref.parent.parent?.id;
        
        if (!brandMap.has(slug)) {
          brandMap.set(slug, {
            name: brand.trim(),
            itemCount: 0,
            itemTypes: new Set(),
          });
          userItemCounts.set(slug, new Set());
          wishlistItemCounts.set(slug, 0);  // Start at 0 items
          collectionNames.set(slug, new Set());
        }

        const brandData = brandMap.get(slug)!;
        brandData.itemCount++;
        
        if (data.itemType) {
          brandData.itemTypes.add(data.itemType);
        }

        if (userId) {
          if (data.onWishlist) {
            // Count total wishlisted ITEMS (not unique users)
            wishlistItemCounts.set(slug, (wishlistItemCounts.get(slug) || 0) + 1);
          } else {
            // Count unique USERS who own items (collectors)
            userItemCounts.get(slug)!.add(userId);
          }
        }

        if (data.collectionName || data.collection) {
          collectionNames.get(slug)!.add(data.collectionName || data.collection);
        }

        processed++;
        if (processed % 1000 === 0) {
          setStatus(`Scanned ${processed} items...`);
        }
      });

      addLog(`✅ Scan complete! Found ${brandMap.size} unique brands`);
      setStatus('Creating brand pages...');
      addLog('🏰 Creating brand pages...');

      let created = 0;
      let updated = 0;
      let errors = 0;

      for (const [slug, data] of brandMap) {
        try {
          // Look for an existing brand under TWO checks before creating:
          //   1. Doc id matches the slug (fast path).
          //   2. Some other doc has this slug in its `slug` field — typically
          //      a claimed brand at an auto-generated doc id like
          //      "rg2rE8mw4XCX0o4ozioo" whose slug is the same as ours.
          // Without (2), this tool used to create a duplicate community
          // brand at brands/{slug} alongside the claimed brand, leaving the
          // user with two docs sharing one URL.
          const brandRef = doc(db, 'brands', slug);
          const existingByDocId = await getDoc(brandRef);

          let targetRef = existingByDocId.exists() ? brandRef : null;

          if (!targetRef) {
            // Slug-field lookup. Will commonly find claimed/verified brands
            // that don't share a doc id with their slug.
            const slugQuery = await getDocs(
              query(collection(db, 'brands'), where('slug', '==', slug))
            );
            if (slugQuery.size > 0) {
              targetRef = slugQuery.docs[0].ref;
              if (slugQuery.size > 1) {
                addLog(`⚠️ Multiple brand docs share slug "${slug}" — updating stats on the first (${targetRef.id}). Consider merging the duplicates via /admin/merge-brand-docs.`);
              }
            }
          }

          if (targetRef) {
            // Update stats on the existing brand doc (whether found by doc id
            // or by slug-field query). We never overwrite name/status/owner
            // fields on an existing doc — those belong to the brand owner.
            await setDoc(targetRef, {
              stats: {
                totalItems: data.itemCount,
                totalCollectors: userItemCounts.get(slug)?.size || 0,
                totalWishlisted: wishlistItemCounts.get(slug) || 0,
                collectionsCount: collectionNames.get(slug)?.size || 0,
                lastUpdated: new Date()
              },
              updatedAt: new Date()
            }, { merge: true });
            updated++;
          } else {
            // No existing brand under either check — safe to create a new
            // community brand at brands/{slug}.
            const brandDoc = {
              name: data.name,
              slug: slug,
              nameLower: data.name.toLowerCase(),
              status: 'community',
              stats: {
                totalItems: data.itemCount,
                totalCollectors: userItemCounts.get(slug)?.size || 0,
                totalWishlisted: wishlistItemCounts.get(slug) || 0,
                collectionsCount: collectionNames.get(slug)?.size || 0,
                lastUpdated: new Date()
              },
              followerCount: 0,
              createdAt: new Date(),
              updatedAt: new Date()
            };

            await setDoc(brandRef, brandDoc);
            created++;
          }

          if ((created + updated) % 50 === 0) {
            setStatus(`Created ${created}, updated ${updated} brands...`);
          }

        } catch (err: any) {
          addLog(`❌ Error with "${data.name}": ${err.message}`);
          errors++;
        }
      }

      // Final results
      addLog('');
      addLog('='.repeat(50));
      addLog('🎉 BRAND POPULATION COMPLETE!');
      addLog('='.repeat(50));
      addLog(`✅ Created: ${created} new brands`);
      addLog(`🔄 Updated: ${updated} existing brands`);
      if (errors > 0) {
        addLog(`❌ Errors: ${errors}`);
      }

      // Top 10 brands
      const sorted = [...brandMap.entries()]
        .sort((a, b) => b[1].itemCount - a[1].itemCount)
        .slice(0, 10);
      
      setTopBrands(sorted.map(([_slug, data]) => ({
        name: data.name,
        count: data.itemCount,
        types: [...data.itemTypes].join(', ') || 'unknown'
      })));

      setStatus(`Done! Created ${created}, updated ${updated} brands.`);

    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
      setStatus('Error occurred - check logs');
    }

    setIsRunning(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="glass-card p-6">
        <h1 className="text-2xl font-bold text-glass mb-2 font-heading">🏰 Populate Brands</h1>
        <p className="text-glass-secondary mb-6 font-body">
          This will scan all items in the database and create brand pages for each unique brand.
        </p>

        <div className="mb-6">
          <button
            onClick={populateBrands}
            disabled={isRunning}
            className={`px-6 py-3 rounded-lg font-medium text-white transition-colors font-body ${
              isRunning
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {isRunning ? 'Running...' : 'Start Population'}
          </button>
        </div>

        {/* Status */}
        <div className="mb-4 p-3 bg-slate-700/40 rounded-lg">
          <span className="font-medium text-glass">Status:</span> <span className="text-glass-secondary">{status}</span>
        </div>

        {/* Top Brands */}
        {topBrands.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-glass mb-3 font-body">📊 Top 10 Brands</h2>
            <div className="glass-card-sm p-4">
              {topBrands.map((brand, i) => (
                <div key={brand.name} className="flex justify-between py-1 border-b border-slate-600 last:border-0">
                  <span>
                    <span className="font-medium text-glass">{i + 1}. {brand.name}</span>
                    <span className="text-glass-muted text-sm ml-2">({brand.types})</span>
                  </span>
                  <span className="text-purple-400 font-medium">{brand.count} items</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logs */}
        {results.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-glass mb-3 font-body">📋 Log</h2>
            <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm max-h-64 overflow-y-auto border border-slate-700">
              {results.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 p-4 bg-amber-500/20 border border-amber-500/50 rounded-lg">
          <p className="text-amber-200 text-sm font-body">
            ⚠️ <strong className="text-amber-100">Remember:</strong> Delete this page after use! Remove the route from App.tsx and delete this file.
          </p>
        </div>
      </div>
    </div>
  );
}
